<script setup lang="ts">
import 'select2';

import $ from 'jquery';
import isEqual from 'lodash/isEqual';
import type { PropType } from 'vue';
import { onBeforeUnmount, onMounted, shallowRef, unref, watch } from 'vue';

import { bindCustomSelect } from '#public/commons-global';
import { type ComponentState, useGenerateIds, useJqueryEventListener } from '#public_ts/composables';

import updateDataAttributes from './data-attributes';

type SelectType = 'default' | 'custom' | string[] | Record<string, string> | Map<string, string>;
type Select2OptionsEx = Partial<Select2Options> & { nousertext?: boolean; autofilter?: boolean };
const props = defineProps({
    id: { type: String as PropType<string | undefined>, required: false, default: undefined },
    name: { type: String as PropType<string | undefined>, required: false, default: undefined },
    modelValue: { type: [String, Number, Array, null] as PropType<string | number | string[] | null | undefined>, required: false, default: undefined },
    options: { type: Object as PropType<Select2OptionsEx | undefined>, required: false, default: undefined },
    type: { type: [String, Array, Object, Map] as PropType<SelectType | undefined>, required: false, default: undefined },
    disabled: { type: Boolean as PropType<boolean | undefined>, required: false, default: undefined },
    readonly: { type: Boolean as PropType<boolean | undefined>, required: false, default: undefined },
    state: { type: Object as PropType<ComponentState | undefined>, required: false, default: undefined },
    data: { type: Object as PropType<Record<string, unknown> | undefined>, required: false, default: undefined },
    classNameOverride: { type: String as PropType<string | undefined>, required: false, default: undefined },
});

const emit = defineEmits<{
    (event: 'update:modelValue', value: string | string[] | undefined): void;
}>();

const selectElement = shallowRef<HTMLElement | undefined>();
let selectedValue = unref(typeof props.modelValue === 'number' ? String(props.modelValue) : props.modelValue);
const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);

const publicApi = {
    selectElement,
};
defineExpose(publicApi);

function checkUpdateState() {
    const _selectElement = unref(selectElement);
    if(_selectElement) {
        $(_selectElement).select2('enable', !props.disabled).select2('readonly', !!props.readonly);
    }
}
function checkUpdateValue() {
    const _selectElement = unref(selectElement);
    if(_selectElement) {
        const val = $(_selectElement).select2('val') as string | string[] | undefined;
        if(val !== selectedValue) {
            selectedValue = val;
            emit('update:modelValue', selectedValue);
        }
    }
}
function init() {
    const _selectElement = unref(selectElement);
    if(_selectElement) {
        if(!props.type || (props.type === 'default') || (props.type === 'custom')) {
            $(_selectElement).select2(props.options || {
                dropdownAutoWidth: true,
            });
        } else {
            bindCustomSelect($(_selectElement), props.type, props.options);
        }
        checkUpdateState();
        if(selectedValue) {
            $(_selectElement).select2('val', selectedValue);
        } else {
            checkUpdateValue();
        }
        if(props.data) {
            updateDataAttributes($(_selectElement), props.data);
        }
    }
}

function reinit() {
    const _selectElement = unref(selectElement);
    if(_selectElement) {
        $(_selectElement).select2('destroy');
        init();
    }
}

const onUpdate = props.state?.onUpdate(reinit);

onMounted(init);
onBeforeUnmount(() => {
    const _selectElement = unref(selectElement);
    if(_selectElement) {
        $(_selectElement).select2('destroy');
    }
    onUpdate?.off();
});

watch([() => props.options, () => props.type], () => {
    reinit();
});

useJqueryEventListener(selectElement, 'change auto-changed', checkUpdateValue);
watch(() => props.modelValue, (val) => {
    const val0 = typeof val === 'number' ? String(val) : val;
    const _selectElement = unref(selectElement);
    if(_selectElement && !isEqual(val0, selectedValue)) {
        selectedValue = val0;
        $(_selectElement).select2('val', selectedValue);
        checkUpdateValue();
    }
});

watch([() => props.disabled, () => props.readonly], checkUpdateState);

watch(() => props.data, (val) => {
    const _selectElement = unref(selectElement);
    if(_selectElement && val) {
        updateDataAttributes($(_selectElement), val);
    }
}, { deep: true });
</script>

<template>
    <select
        v-if="!props.type || (props.type === 'default')"
        :id="controlId"
        ref="selectElement"
        :name="controlName"
        class="select2-enabled select2-auto"
        :class="(props.classNameOverride !== undefined) ? props.classNameOverride : 'form-control'"
    >
        <slot />
    </select>
    <input
        v-else
        :id="controlId"
        ref="selectElement"
        :name="controlName"
        type="hidden"
        class="select2-enabled select2-auto"
        :class="(props.classNameOverride !== undefined) ? props.classNameOverride : 'form-control'"
    >
</template>
